import { faUser as fasUser } from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { Config } from "@config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CityData } from "@utils/city-id";
import { logCustomError } from "@utils/sentry-error";
import autobind from "autobind-decorator";
import {
	FormCheck,
	FormCity,
	FormEmail,
	FormInput,
	FormPhone,
	ProgressButton,
	__,
	getLocale,
	phoneFormatter,
} from "bm-ui-kit";
import type { FormApi } from "final-form";
import compact from "lodash/compact";
import type { ReactNode } from "react";
import { Card, Col, Row } from "react-bootstrap";
import type { AbstractSubmitStepProps } from "./AbstractSubmitStep";
import { AbstractSubmitStep } from "./AbstractSubmitStep";
import { validFirstName, validLastName } from "./BasicLeadForm.fn";

export interface LeadFormStepFields {
	lastName: string;
	firstName: string;
	email: string;
	phone: string;
	cityName: string;
	cityId?: number;
	wantsCrossSell?: boolean;
	wantsNewsletter?: boolean;
}

export interface LeadFormProps extends AbstractSubmitStepProps {
	title?: ReactNode;
	button?: ReactNode;
	crossSellOption?: boolean;
	newsletterOption?: boolean;
	newsletterText?: string;
	hideCityNameField?: boolean;
}

export class BasicLeadForm<
	StepFields extends LeadFormStepFields = LeadFormStepFields,
	Props extends LeadFormProps = LeadFormProps,
> extends AbstractSubmitStep<StepFields, Props> {
	public static defaultProps = {
		...AbstractSubmitStep.defaultProps,
		title: __("Kérj ingyenes segítséget szakértőnktől!") as ReactNode,
		button: __("Visszahívást kérek"),
	};

	protected initValues(): StepFields {
		return {
			lastName: Config.getCurrentUser().getLastName(),
			firstName: Config.getCurrentUser().getFirstName(),
			email: Config.getCurrentUser().getEmail(),
			phone: phoneFormatter(Config.getCurrentUser().getPhoneNumber()),
			cityName: Config.getCurrentUser().getCityName(),
			cityId: undefined,
		} as unknown as StepFields;
	}

	protected renderContent(_values: StepFields, _form: FormApi<StepFields>): ReactNode {
		const { button, submitState, colorVersion } = this.props;

		let bgClassName = "";

		if (colorVersion) {
			// bg-dark is intentional when colorVersion is 'black', bg-black is only for the header in this case
			bgClassName = colorVersion === "black" ? "bg-dark" : "bg-white";
		}

		return (
			<>
				{this.renderHeader()}
				<Card.Body className={`${bgClassName} d-flex flex-column`}>
					{this.renderFields()}
					{this.renderCheckboxes()}
					{this.renderPrivacyStatement()}
				</Card.Body>
				<Card.Footer className={`${bgClassName} text-center`}>
					<ProgressButton
						type="submit"
						data-testid="button-submit"
						variant={colorVersion ? "primary" : "gold"}
						size="lg"
						block={!!colorVersion}
						state={submitState}
						className="font-weight-bold"
					>
						{button}
					</ProgressButton>
				</Card.Footer>
			</>
		);
	}

	protected renderHeader(): ReactNode {
		const { title, colorVersion } = this.props;

		if (!title) {
			return null;
		}

		return (
			<Card.Header className={colorVersion === "white" ? "card-header-gray py-2 h4" : ""}>
				<div className={`${colorVersion === "white" ? "" : "h4"}  mb-0 text-center`}>{title}</div>
			</Card.Header>
		);
	}

	protected renderFields(): ReactNode {
		const disabled = this.isDisabled();

		return (
			<>
				<Row>
					<Col className="pr-1" xs={6}>
						<FormInput
							placeholder="Vezetéknév"
							name="lastName"
							prepend={<FontAwesomeIcon icon={fasUser} />}
							validate={validLastName}
							disabled={disabled}
							required
						/>
					</Col>
					<Col className="pl-1" xs={6}>
						<FormInput
							placeholder="Keresztnév"
							name="firstName"
							prepend={<FontAwesomeIcon icon={fasUser} />}
							validate={validFirstName}
							disabled={disabled}
							required
						/>
					</Col>
				</Row>
				<FormEmail placeholder="E-mail cím" name="email" disabled={disabled} required />
				<FormPhone placeholder="Telefonszám" name="phone" autoComplete="tel" disabled={disabled} required />
				{this.renderCityField()}
			</>
		);
	}

	protected renderCityField(): ReactNode {
		const { hideCityNameField = false } = this.props;

		if (hideCityNameField) {
			return null;
		}

		const disabled = this.isDisabled();

		return <FormCity placeholder="Lakhely" name="cityName" disabled={disabled} required />;
	}

	protected renderPrivacyStatement(): ReactNode {
		const { colorVersion } = this.props;

		return (
			<div className={`${colorVersion === "white" ? "text-dark" : ""} text-center fs-xs pt-2 mt-auto `}>
				{__("A lenti gomb megnyomásával elfogadom az")}{" "}
				<a
					className="text-reset text-underline"
					href={Config.getBankmonitorUrl("/adatvedelem/")}
					// biome-ignore lint/a11y/noBlankTarget: in-page link
					target="_blank"
				>
					{__("adatvédelmi tájékoztatóban")}
				</a>
				{getLocale() === "hu_HU" && " foglaltakat."}
			</div>
		);
	}

	protected renderCheckboxes(): ReactNode {
		const { crossSellOption, newsletterOption, newsletterText, colorVersion } = this.props;
		const classNameString = `mb-1 font-weight-bold ${colorVersion === "white" ? "text-dark" : ""}`;

		return (
			<>
				{crossSellOption && (
					<FormCheck
						className={classNameString}
						label="Szeretnék a termékről, vagy kapcsolódó termékekről tájékoztatást kapni a jövőben."
						name="wantsCrossSell"
					/>
				)}
				{newsletterOption && (
					<FormCheck
						className={classNameString}
						label={
							newsletterText || "Szeretnék hírlevelet kapni az újdonságokról, kiemelt banki ajánlatokról."
						}
						name="wantsNewsletter"
					/>
				)}
			</>
		);
	}

	protected getClassName(): string {
		const { colorVersion } = this.props;

		return compact([super.getClassName(), colorVersion === "black" ? undefined : "bg-success"]).join(" ");
	}

	@autobind
	protected onSubmit(data: StepFields, realSubmit = true): void {
		if (realSubmit) {
			CityData.getCityIdByName(data.cityName)
				.then((cityId) => {
					super.onSubmit({ ...data, cityId });
				})
				.catch(() => {
					super.onSubmit({ ...data, cityId: undefined });

					logCustomError("Missing city id during lead submit!", { cityName: data.cityName }, "error");
				});
		} else {
			super.onSubmit({ ...data, cityId: undefined });
		}
	}

	protected getComponentName(): string {
		return "BasicLeadForm";
	}
}
